import React, { useLayoutEffect, useRef } from "react";
import ReportStepFrame from "components/report/ReportStepFrame";
import { ReportStepScore } from "components/report/ReportShell";
import { carryScoreParts, ScorePartRects } from "components/report/score-carry";
import styles from "./SummaryNarrativeStep.module.scss";

const prefersReducedMotion = (): boolean =>
  Boolean(window.matchMedia?.("(prefers-reduced-motion: reduce)").matches);

export interface SummaryNarrativeStepProps {
  /** The headline above the narrative. */
  title: string;
  /** The generated account of the learner's result. Length is unpredictable. */
  narrative: string;
  /** Who the narrative is attributed to, e.g. Ori. Omit to drop the byline. */
  attribution?: string;
  /**
   * The byline's mark, as a URL the caller imports. Artwork stays out of the
   * component so the step never pins a particular asset.
   */
  attributionIconSrc?: string;
  /** The secondary block's own heading. */
  secondaryTitle: string;
  /**
   * The secondary block's prose. Also of variable length. Empty leaves the
   * whole block out rather than drawing a card with a title and nothing in
   * it, since the copy for it is generated and absent is a real state.
   * While `pending` the block is drawn with a placeholder instead, because
   * whether it will have copy is not known until generation finishes.
   */
  secondaryBody?: string;
  /**
   * The copy is still being written. Both cards hold their place with a
   * placeholder instead of prose, so the copy landing fills them in rather
   * than rewriting a sentence the learner had started on.
   */
  pending?: boolean;
  /** What the narrative card says while `pending`. */
  pendingLabel?: string;
  /**
   * The learner's score, shown at the top of the step rather than in the
   * shell's corner: this is the screen the reveal hands the number to.
   */
  score: ReportStepScore;
  /** The line above the score, e.g. "You scored". */
  scoreEyebrow: string;
  /**
   * Where the reveal left each part of the score, when this step follows it.
   * The score then travels from there to its place here while the rest of
   * the step fades in. Absent, the step simply appears with the score in
   * place, as it does when the learner steps back to it.
   */
  carriedFrom?: ScorePartRects;
}

/**
 * One block standing in for prose that has not been written yet, as the rest
 * of the app's skeletons are: a single rounded block with a gentle pulse.
 */
const Placeholder: React.FC<{ size: "narrative" | "secondary" }> = ({
  size,
}) => (
  <span
    className={`${styles.placeholder} ${styles[`placeholder-${size}`]}`}
    aria-hidden="true"
    data-testid="report-summary-placeholder"
  />
);

/**
 * The step that explains the learner's score in words.
 *
 * It pairs what the measurement says, attributed to Ori, with the older
 * practice behind it.
 *
 * Both prose blocks hold generated copy of unknown length, so nothing here
 * is given a fixed height: the blocks grow instead, and the step scrolls
 * rather than clipping the sentence someone needs to read. The design's
 * fixed card heights are the shape of one sample, not a constraint.
 */
const SummaryNarrativeStep: React.FC<SummaryNarrativeStepProps> = ({
  title,
  narrative,
  attribution,
  attributionIconSrc,
  secondaryTitle,
  secondaryBody,
  pending = false,
  pendingLabel,
  score,
  scoreEyebrow,
  carriedFrom,
}) => {
  const headRef = useRef<HTMLDivElement>(null);
  const arriving = Boolean(carriedFrom) && !prefersReducedMotion();

  // Once, as the step first paints, so the score starts where the reveal
  // left it rather than being drawn at home for a frame first. Copy landing
  // later re-renders the step without moving the score again.
  useLayoutEffect(() => {
    if (arriving && carriedFrom && headRef.current) {
      carryScoreParts(headRef.current, carriedFrom);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ReportStepFrame arriving={arriving}>
      <div
        className={styles.step}
        data-testid="report-summary-narrative-step"
        data-arriving={arriving}
      >
        <div
          ref={headRef}
          className={styles["score-head"]}
          data-testid="report-summary-score"
        >
          <p className={styles["score-eyebrow"]} data-score-part="eyebrow">
            {scoreEyebrow}
          </p>
          {/*
            Spans the carry can move one by one: the reveal sets the slash
            smaller against the digits than this does, so scaling the reading
            as a whole would start it out of proportion.
          */}
          <p className={styles["score-value"]}>
            <span className={styles["score-number"]} data-score-part="score">
              {score.value}
            </span>
            <span
              className={styles["score-separator"]}
              data-score-part="separator"
            >
              /
            </span>
            <span className={styles["score-out-of"]} data-score-part="out-of">
              {score.outOf ?? 100}
            </span>
          </p>
        </div>

        <h1 className={styles.title}>{title}</h1>

        <section className={styles.card} aria-busy={pending}>
          {attribution && (
            <p className={styles.attribution}>
              {attributionIconSrc && (
                <img src={attributionIconSrc} alt="" className={styles.mark} />
              )}
              <span>{attribution}</span>
            </p>
          )}
          {pending ? (
            <>
              <p className={styles.narrative} role="status">
                {pendingLabel}
              </p>
              <Placeholder size="narrative" />
            </>
          ) : (
            <p className={styles.narrative}>{narrative}</p>
          )}
        </section>

        {(pending || secondaryBody) && (
          <section className={styles.card} aria-busy={pending}>
            <h2 className={styles["card-title"]}>{secondaryTitle}</h2>
            {pending ? (
              <Placeholder size="secondary" />
            ) : (
              <p className={styles["card-body"]}>{secondaryBody}</p>
            )}
          </section>
        )}
      </div>
    </ReportStepFrame>
  );
};

export default SummaryNarrativeStep;
